/**
 * The Core reducer is intended to contain any data core to the running of the app.
 *
 * If patterns in the core data are recognised that would be better served by another slice, then
 * extract a separate slice.
 */
import { isLogLevelName, LogLevel } from '@dora/lumberjack';
import * as RTK from '@reduxjs/toolkit';
import { AppConfig } from '../data/config';
import * as Rm from '../data/room';
import { TagManagerArgs } from 'react-gtm-module';
import { AppError } from '../data/appError';
import * as AR from '@dora/arctic';
import { PropertyValue } from '../components/configuration/types';

export type View = 'default' | 'start' | 'scene' | 'inspiration_runway' | 'configuration';
export type SpaceLaunchData = { ar: boolean } & (
    | {
          /** Product set the user wishes to load upon starting Space */
          sku: string;
      }
    | {
          /** Layout the user wishes to load upon starting Space */
          layoutId: string;
      }
);

/** Data associated with the Visual Space journey */
interface SpaceState {
    mode: 'space';
    launchData: SpaceLaunchData;
    room: Rm.SerialisedRoom | undefined;
}

export interface InspirationState {
    mode: 'inspiration';
    launchData: {
        /** The productId of the AR Model to load */
        productId: string;
        /** The token required to download individual models */
        token: string;
        /** Whether or not to open the AR modal on start */
        shouldOpenArModal: boolean;
        placement: AR.Placement;
        dimensionsVisibleInViewer: boolean;
        modalText: AR.ModalText;
    };
}

/** Data associated with the Visual Configuration journey */
interface ConfigurationState {
    mode: 'configuration';
    launchData: { ar: boolean } & (
        | {
              // the id of the configurator definition to use
              configuratorId: string;
              startWithSelections: Record<string, PropertyValue>;
          }
        | {
              // The id of the saved configuration to load
              configurationId: string;
          }
    );
}

export interface State {
    /**
     * Is the app currently open and on display, or closed and invisible?
     */
    open: boolean;
    /**
     * The currently focused range of furniture.
     * Undefined until set as there is no sensible default
     */
    range: string | undefined;

    /**
     * The view to display in the app.
     */
    view: View;

    /**
     * SpaceState is required when the view is 'scene', inspiration for 'inspiration_runway', configuration for 'configuration'.
     *
     * The types could certainly capture this better.
     */
    viewData: SpaceState | InspirationState | ConfigurationState | undefined;

    /** Unique client id string used to fetch product and asset data etc */
    clientId: string | undefined;

    /** ISO locale string used for i18n */
    locale: string | undefined;

    /** ISO currency string used for price displays */
    currency: string | undefined;

    /** Deployed environment we are in */
    environment: 'development' | 'production' | undefined;

    /** Google tag manager details */
    gtm: TagManagerArgs | undefined;

    /**
     * URLs for each of the services the app requires. Likely to be set via the AppConfig object.
     */
    serviceUrls?: {
        /** using toast */
        models: string;
        /** using sparrow */
        ar: string;
        /** using gift */
        productData: string;
        /** using hyrule */
        shortLink: {
            /** the service layer for generating/manipulating short links */
            service: string;
            /** the URL for accessing or using a shortLink */
            frontend: string;
        };
        /** using haymaker */
        layout: string;
        /** using frankenstein */
        modelGenerator: string;
        /** using gavel */
        configuration: string;
        /** using gluon */
        particleMapping: string;
    };
    /** URL user is viewing the app from, used to define URL to return to encoded in shortLink
     * generated while saving a layout generated while saving a layout. For clarity, this is the URL
     * outside of the iframe (e.g.
     * https://www.modafurnishings.co.uk/talia-cubo-6-10-seat-dining-set-with-parasol)
     *
     * TODO: should start as undefined
     */
    currentUrl: string;

    /**
     * The over-all loading state of the app. This will be dependent on other factors, but we need a
     * single and central value to represent loading state to enable stable and accurate loading
     * screens that only show up after different aspects of the scene start to load.
     */
    loading: boolean;

    /**
     * The current log level of Dora
     */
    logLevel: LogLevel;

    /**
     * an app-wide error, used to determine if something catastrophic has happened at the app-wide
     * level that requires user awareness or intervention
     */
    error: AppError | undefined;

    /**
     * An indication of the AR Capability of the current device
     */
    arCompatibility: AR.Compatibility | undefined;

    /**
     * Detected device information at the SDK layer
     */
    device:
        | {
              isAndroid: boolean;
              isIOS: boolean;
              isMobile: boolean;
              isSafari: boolean;
              isFirefox: boolean;
          }
        | undefined;

    /**
     * Feature flags passed from SDK
     */
    featureFlags: Record<string, boolean>;
}

export const initialState: State = {
    open: false,
    range: undefined,
    clientId: undefined,
    locale: undefined,
    currency: undefined,
    environment: undefined,
    gtm: undefined,
    view: 'default',
    viewData: undefined,
    currentUrl: '',
    loading: true,
    logLevel: LogLevel.Info,
    error: undefined,
    arCompatibility: undefined,
    device: undefined,
    featureFlags: {},
};

export const slice = RTK.createSlice({
    name: 'core',
    initialState,
    reducers: {
        setRange(state, action: RTK.PayloadAction</** Range */ string>) {
            state.range = action.payload;
        },
        setView(state, action: RTK.PayloadAction<View>) {
            state.view = action.payload;
        },
        /** Trigger the bootstrapping of the application */
        bootstrap(state, action: RTK.PayloadAction<AppConfig>) {
            state.view = 'start';
            const {
                arUrl,
                toastUrl,
                giftUrl,
                hyruleUrl,
                haymakerUrl,
                frankensteinUrl,
                gavelUrl,
                gluonUrl,
                dbhqUrl,
                locale,
                currency,
                clientId,
                environment,
                gtmArgs,
                logLevel: logLevelString,
                ar: { compatibility },
                device: { isAndroid, isIOS, isMobile, isSafari, isFirefox },
                featureFlags,
            } = action.payload;

            state.clientId = clientId;
            state.locale = locale;
            state.currency = currency;
            state.environment = environment;
            state.gtm = gtmArgs;
            state.logLevel = isLogLevelName(logLevelString)
                ? LogLevel[logLevelString]
                : LogLevel.Info;
            state.serviceUrls = {
                models: toastUrl,
                ar: arUrl,
                productData: giftUrl,
                shortLink: { service: hyruleUrl, frontend: dbhqUrl },
                layout: haymakerUrl,
                modelGenerator: frankensteinUrl,
                configuration: gavelUrl,
                particleMapping: gluonUrl,
            };
            state.device = { isAndroid, isIOS, isMobile, isSafari, isFirefox };
            state.arCompatibility = compatibility;
            state.featureFlags = featureFlags;
        },
        /** Trigger the 'startSpace' functionality of the application,
         *  loading an initial product or product set.
         */
        startSpaceFromSku(
            state,
            action: RTK.PayloadAction<{
                sku: string;
                currentUrl: string;
            }>,
        ) {
            state.currentUrl = action.payload.currentUrl;
            state.view = 'scene';
            state.viewData = {
                mode: 'space',
                launchData: { ar: false, sku: action.payload.sku },
                room: undefined,
            };
        },
        /** Trigger the 'startSpace' functionality of the application,
         *  loading an initial layout
         */
        startSpaceFromLayout(
            state,
            action: RTK.PayloadAction<{
                layoutId: string;
                currentUrl: string;
                ar: boolean;
            }>,
        ) {
            state.currentUrl = action.payload.currentUrl;
            state.view = 'scene';
            state.viewData = {
                mode: 'space',
                launchData: { ar: action.payload.ar, layoutId: action.payload.layoutId },
                room: undefined,
            };
        },

        /**
         * Start the Inspiration journey in Runway mode.
         *
         * Runway mode is used when the user is landing on the page on a compatible device after
         * following a Visual Inspiration link (i.e. following a QR code for View in AR, or a saved
         * link for similar).
         */
        startInspirationRunway(
            state,
            action: RTK.PayloadAction<{
                productId: string;
                token: string;
                placement: AR.Placement;
                shouldOpenArModal: boolean;
                dimensionsVisibleInViewer: boolean;
                modalText: AR.ModalText;
                currentUrl: string;
            }>,
        ) {
            state.view = 'inspiration_runway';
            state.viewData = {
                mode: 'inspiration',
                launchData: { ...action.payload },
            };
            state.currentUrl = action.payload.currentUrl;
        },
        /** Start the Configuration mode of the application with a configurator Id.
         */
        startConfiguration(
            state,
            action: RTK.PayloadAction<{
                configuratorId: string;
                startWithSelections: Record<string, PropertyValue>;
                currentUrl: string;
            }>,
        ) {
            state.currentUrl = action.payload.currentUrl;
            state.view = 'configuration';
            state.viewData = {
                mode: 'configuration',
                launchData: { ...action.payload, ar: false },
            };
        },
        startConfigurationFromSave(
            state,
            action: RTK.PayloadAction<{
                configurationId: string;
                currentUrl: string;
                ar: boolean;
            }>,
        ) {
            state.currentUrl = action.payload.currentUrl;
            state.view = 'configuration';
            state.viewData = {
                mode: 'configuration',
                launchData: {
                    configurationId: action.payload.configurationId,
                    ar: action.payload.ar,
                },
            };
        },
        /**
         * Set details about the room in Visual Space mode
         */
        setSpaceRoom(state, action: RTK.PayloadAction<Rm.Room>) {
            if (state.viewData?.mode === 'space') {
                state.viewData = {
                    ...state.viewData,
                    mode: 'space',
                    room: Rm.serialise(action.payload),
                };
            }
        },
        /** Clear the space data used to launch the app - this is intended to be used on closing the
         *  app in preparation for a fresh start.
         */
        clearSpace(state, _: RTK.Action) {
            if (state.viewData?.mode === 'space') {
                state.viewData = undefined;
            }
        },
        /** Reset view data and view */
        clearViewData(state, _: RTK.Action) {
            state.viewData = undefined;
            state.view = 'default';
        },
        setOpen(state, _: RTK.Action) {
            state.open = true;
        },
        setClosed(state, _: RTK.Action) {
            state.open = false;
        },
        setLoadingComplete(state, _: RTK.Action) {
            state.loading = false;
        },
        requireLoading(state, _: RTK.Action) {
            state.loading = true;
        },
        error(state, action: RTK.PayloadAction<AppError>) {
            state.error = action.payload;
        },
        clearError(state, _: RTK.Action) {
            state.error = undefined;
        },
    },
});

export const {
    setView,
    bootstrap,
    startSpaceFromSku,
    startSpaceFromLayout,
    startInspirationRunway,
    startConfiguration,
    startConfigurationFromSave,
    clearSpace,
    clearViewData,
    setSpaceRoom,
    setRange,
    setOpen,
    setClosed,
    setLoadingComplete,
    requireLoading,
    error,
    clearError,
} = slice.actions;

export const reducer = slice.reducer;
